import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Row, Col, Card, Table, Tag, Typography, Space, Select, Input, Button,
  Badge, Tooltip, Alert, Divider, Drawer, Progress
} from 'antd';
import {
  WifiOutlined, DisconnectOutlined, SearchOutlined, DownloadOutlined,
  ReloadOutlined, UserOutlined, TeamOutlined, RadarChartOutlined,
  EnvironmentOutlined, EyeOutlined, ArrowUpOutlined, ArrowDownOutlined
} from '@ant-design/icons';
import {
  getLiveLocationCounts, getLiveDelegates, getLiveReaderStatus, getLiveStats, getEvents
} from '../api';
import useWebSocket from '../hooks/useWebSocket';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { Title, Text } = Typography;
const { Option } = Select;

const PALETTE = {
  primaryBlue: '#2563eb',
  darkNavy:    '#1e293b',
  lightBg:     '#f1f5f9',
  green:       '#10b981',
  purple:      '#7c3aed',
  orange:      '#f59e0b',
  red:         '#ef4444',
};

// Inject hover CSS
if (typeof document !== 'undefined' && !document.getElementById('loc-card-css')) {
  const s = document.createElement('style');
  s.id = 'loc-card-css';
  s.textContent = `.loc-card { transition: transform 0.18s, box-shadow 0.18s; cursor: pointer; } .loc-card:hover { transform: scale(1.03); box-shadow: 0 6px 24px rgba(37,99,235,0.18) !important; } .loc-card:hover .loc-eye { opacity: 1 !important; }`;
  document.head.appendChild(s);
}

// ── Live Clock ────────────────────────────────────────────────────────────────
function LiveClock() {
  const [now, setNow] = useState(dayjs());
  useEffect(() => {
    const t = setInterval(() => setNow(dayjs()), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <div style={{
      textAlign: 'center', padding: '14px 28px',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e40af 50%, #2563eb 100%)',
      borderRadius: 14, minWidth: 200,
      boxShadow: '0 4px 20px rgba(37,99,235,0.35)',
    }}>
      <div style={{ color: '#fff', fontSize: 34, fontWeight: 800, fontFamily: 'monospace', letterSpacing: 4, lineHeight: 1.1 }}>
        {now.format('HH:mm:ss')}
      </div>
      <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, marginTop: 4 }}>
        {now.format('dddd, DD MMMM YYYY')}
      </div>
    </div>
  );
}

// ── Location Card ─────────────────────────────────────────────────────────────
function LocationCard({ loc, onClick }) {
  const pct   = loc.capacity > 0 ? Math.min(100, Math.round((loc.delegate_count / loc.capacity) * 100)) : 0;
  const color = pct > 90 ? PALETTE.red : pct > 70 ? PALETTE.orange : PALETTE.green;
  return (
    <Card size="small" className="loc-card" onClick={() => onClick(loc)}
      style={{ borderRadius: 12, borderLeft: `4px solid ${color}`, height: '100%', position: 'relative' }}>
      <div className="loc-eye" style={{ position: 'absolute', top: 8, right: 8, opacity: 0, color: PALETTE.primaryBlue, fontSize: 14, pointerEvents: 'none', transition: 'opacity 0.18s' }}>
        <EyeOutlined />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1, paddingRight: 16 }}>
          <Text strong style={{ fontSize: 12 }}>{loc.location_name}</Text><br />
          <Text type="secondary" style={{ fontSize: 10 }}>{loc.location_code} · {loc.location_type}</Text>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 26, fontWeight: 800, color, lineHeight: 1 }}>{loc.delegate_count || 0}</div>
          <Text type="secondary" style={{ fontSize: 10 }}>/ {loc.capacity || '∞'}</Text>
        </div>
      </div>
      {loc.capacity > 0 && (
        <Progress percent={pct} strokeColor={color} trailColor="#f0f0f0" size="small" showInfo={false} style={{ marginTop: 8, marginBottom: 0 }} />
      )}
    </Card>
  );
}

// ── Stat Card ─────────────────────────────────────────────────────────────────
function StatCard({ title, value, icon, gradient }) {
  return (
    <Card style={{ borderRadius: 12, background: gradient, border: 'none', boxShadow: '0 4px 16px rgba(0,0,0,0.15)' }}
      size="small" bodyStyle={{ padding: '16px 20px' }}>
      <Space align="center">
        <div style={{ width: 46, height: 46, borderRadius: 12, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, color: '#fff' }}>
          {icon}
        </div>
        <div>
          <div style={{ fontSize: 24, fontWeight: 800, lineHeight: 1, color: '#fff' }}>{value ?? 0}</div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)', marginTop: 2 }}>{title}</div>
        </div>
      </Space>
    </Card>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function LiveTracking() {
  const [events,          setEvents]          = useState([]);
  const [eventId,         setEventId]         = useState(null);
  const [stats,           setStats]           = useState({});
  const [locations,       setLocations]       = useState([]);
  const [delegates,       setDelegates]       = useState([]);
  const [readers,         setReaders]         = useState([]);
  const [loading,         setLoading]         = useState(false);
  const [wsStatus,        setWsStatus]        = useState('connecting');
  const [search,          setSearch]          = useState('');
  const [filterCat,       setFilterCat]       = useState(null);
  const [filterLoc,       setFilterLoc]       = useState(null);
  const [lastRefresh,     setLastRefresh]     = useState(null);
  const [selectedLoc,     setSelectedLoc]     = useState(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    getEvents().then(r => {
      const evList = r.data.data || [];
      setEvents(evList);
      const active = evList.find(e => e.status === 'active') || evList[0];
      if (active) setEventId(active.id);
    }).catch(() => {});
  }, []);

  const fetchAll = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const params = { event_id: eventId };
      const [s, l, d, r] = await Promise.all([
        getLiveStats(params),
        getLiveLocationCounts(params),
        getLiveDelegates({ ...params, search: search || undefined, category_id: filterCat || undefined, location_id: filterLoc || undefined }),
        getLiveReaderStatus(params),
      ]);
      setStats(s.data.data || {});
      setLocations(l.data.data || []);
      setDelegates(d.data.data || []);
      setReaders(r.data.data || []);
      setLastRefresh(dayjs());
    } catch (_) {}
    setLoading(false);
  }, [eventId, search, filterCat, filterLoc]);

  useEffect(() => {
    fetchAll();
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(fetchAll, 5000);
    return () => clearInterval(intervalRef.current);
  }, [fetchAll]);

  // WebSocket for instant updates
  const handleWsScan = useCallback((data) => {
    setWsStatus('connected');
    if (eventId) {
      getLiveStats({ event_id: eventId }).then(r => setStats(r.data.data || {})).catch(() => {});
      getLiveLocationCounts({ event_id: eventId }).then(r => setLocations(r.data.data || [])).catch(() => {});
      getLiveDelegates({ event_id: eventId }).then(r => setDelegates(r.data.data || [])).catch(() => {});
    }
  }, [eventId]);

  useWebSocket(handleWsScan);

  const exportExcel = async () => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Live Tracking');
    ws.addRow(['Delegate Name', 'RFID Tag', 'Category', 'Company', 'Current Location', 'Last Seen']);
    ws.getRow(1).font = { bold: true };
    ws.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F1D6B' } };
    ws.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
    delegates.forEach(d => ws.addRow([
      d.delegate_name, d.rfid_tag, d.category_name, d.company,
      d.current_location || 'N/A',
      d.last_seen ? dayjs(d.last_seen).format('DD-MMM-YY HH:mm:ss') : '-',
    ]));
    const buf = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buf]), `live_tracking_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`);
  };

  const drawerDelegates = selectedLoc
    ? delegates.filter(d => d.current_location === selectedLoc.location_name)
    : [];

  const statCards = [
    { title: 'Total Delegates', value: stats.total_delegates,  icon: <TeamOutlined />,        gradient: 'linear-gradient(135deg, #1e40af, #2563eb)' },
    { title: 'Present Now',     value: stats.present_delegates, icon: <UserOutlined />,         gradient: 'linear-gradient(135deg, #065f46, #10b981)' },
    { title: 'Readers Online',  value: `${stats.readers_online || 0} / ${stats.total_readers || 0}`, icon: <WifiOutlined />, gradient: 'linear-gradient(135deg, #4c1d95, #7c3aed)' },
    { title: 'Scans (1hr)',     value: stats.scans_last_hour,  icon: <RadarChartOutlined />,    gradient: 'linear-gradient(135deg, #92400e, #f59e0b)' },
  ];

  const delegateCols = [
    { title: '#', width: 44, render: (_, __, i) => i + 1 },
    {
      title: 'Delegate Name', render: (_, d) => (
        <Space>
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: d.category_color || '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
            {d.delegate_name?.[0]?.toUpperCase()}
          </div>
          <div>
            <Text strong style={{ fontSize: 12 }}>{d.delegate_name}</Text><br />
            <Text type="secondary" style={{ fontSize: 11 }}>{d.company || '—'}</Text>
          </div>
        </Space>
      )
    },
    { title: 'RFID Band No.', dataIndex: 'rfid_tag', render: v => <Text code style={{ fontSize: 10 }}>{v}</Text> },
    { title: 'Category', dataIndex: 'category_name', width: 110, render: (v, r) => v ? <Tag color={r.category_color}>{v}</Tag> : '—' },
    { title: 'Badge Type', dataIndex: 'badge_type', width: 90, render: v => <Tag>{v || 'Badge'}</Tag> },
    {
      title: 'Current Location', dataIndex: 'current_location', width: 150,
      render: v => v
        ? <Tag icon={<EnvironmentOutlined />} color="blue">{v}</Tag>
        : <Tag color="default">Unknown</Tag>
    },
    {
      title: 'Last Seen', dataIndex: 'last_seen', width: 90,
      render: v => v ? <Text style={{ fontSize: 11 }}>{dayjs(v).format('HH:mm:ss')}</Text> : '—'
    },
  ];

  const drawerCols = [
    { title: 'Name', dataIndex: 'delegate_name', render: v => <Text strong>{v}</Text>, sorter: (a, b) => a.delegate_name.localeCompare(b.delegate_name) },
    { title: 'Category', dataIndex: 'category_name', width: 120, render: (v, r) => <Tag color={r.category_color || '#2563eb'} style={{ fontWeight: 600 }}>{v || '—'}</Tag> },
    { title: 'Company', dataIndex: 'company', render: v => <Text type="secondary">{v || '—'}</Text> },
    { title: 'Last Seen', dataIndex: 'last_seen', width: 90, render: v => v ? <Tag color="geekblue" style={{ fontFamily: 'monospace' }}>{dayjs(v).format('HH:mm:ss')}</Tag> : <Tag color="default">—</Tag> },
  ];

  return (
    <div style={{ background: PALETTE.lightBg, minHeight: '100vh', padding: '0 0 24px' }}>
      {/* Header */}
      <Row gutter={[16, 12]} align="middle" style={{ marginBottom: 16, padding: '16px 24px 0' }}>
        <Col flex="auto">
          <Title level={4} style={{ margin: 0, color: PALETTE.darkNavy }}>
            <RadarChartOutlined style={{ marginRight: 8, color: PALETTE.primaryBlue }} />
            Live Tracking Dashboard
          </Title>
          {lastRefresh && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              Last refreshed: {lastRefresh.format('HH:mm:ss')} ·{' '}
              <Badge color={wsStatus === 'connected' ? '#52c41a' : '#faad14'} text={wsStatus === 'connected' ? 'Live (WebSocket)' : 'Polling every 5s'} />
            </Text>
          )}
        </Col>
        <Col>
          <LiveClock />
        </Col>
      </Row>

      <div style={{ padding: '0 24px' }}>
        {/* Filter bar */}
        <Card style={{ marginBottom: 16, borderRadius: 12 }} size="small">
          <Row gutter={[8, 8]} align="middle">
            <Col xs={24} sm={12} md={5}>
              <Select value={eventId} onChange={v => { setEventId(v); setFilterLoc(null); }} style={{ width: '100%' }} placeholder="Select Event">
                {events.map(e => <Option key={e.id} value={e.id}><Badge color={e.status === 'active' ? 'green' : 'default'} text={e.event_name} /></Option>)}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Input prefix={<SearchOutlined />} placeholder="Search name / RFID / company"
                value={search} onChange={e => setSearch(e.target.value)} allowClear />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Select placeholder="Category" allowClear style={{ width: '100%' }} value={filterCat} onChange={setFilterCat}>
                {[...new Set(delegates.map(d => d.category_name))].filter(Boolean).map(c => (
                  <Option key={c} value={delegates.find(d => d.category_name === c)?.category_id}>{c}</Option>
                ))}
              </Select>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Select placeholder="Location" allowClear style={{ width: '100%' }} value={filterLoc} onChange={setFilterLoc}>
                {locations.map(l => <Option key={l.id} value={l.id}>{l.location_name}</Option>)}
              </Select>
            </Col>
            <Col flex="auto" style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Space>
                <Button icon={<ReloadOutlined />} onClick={fetchAll} loading={loading}>Refresh</Button>
                <Button icon={<DownloadOutlined />} type="primary" onClick={exportExcel}
                  style={{ background: PALETTE.primaryBlue }}>
                  Export Excel
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Capacity alert banner — only when zones are full or near full */}
        {(() => {
          const critical = locations.filter(l => l.capacity > 0 && l.delegate_count >= l.capacity);
          const warn     = locations.filter(l => l.capacity > 0 && l.delegate_count < l.capacity && (l.delegate_count / l.capacity) >= 0.8);
          if (critical.length === 0 && warn.length === 0) return null;
          return (
            <Alert
              type={critical.length ? 'error' : 'warning'}
              showIcon
              banner
              style={{ marginBottom: 16, borderRadius: 8 }}
              message={critical.length
                ? `${critical.length} zone${critical.length > 1 ? 's' : ''} at or over capacity`
                : `${warn.length} zone${warn.length > 1 ? 's' : ''} approaching capacity (≥ 80%)`
              }
              description={
                <Space wrap>
                  {[...critical, ...warn].slice(0, 8).map(l => {
                    const pct = Math.round((l.delegate_count / l.capacity) * 100);
                    const isCrit = l.delegate_count >= l.capacity;
                    return (
                      <Tag key={l.id} color={isCrit ? 'red' : 'orange'}>
                        {l.location_name}: {l.delegate_count}/{l.capacity} ({pct}%)
                      </Tag>
                    );
                  })}
                </Space>
              }
            />
          );
        })()}

        {/* Stat cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          {statCards.map((s, i) => (
            <Col xs={12} md={6} key={i}><StatCard {...s} /></Col>
          ))}
        </Row>

        {/* Reader connection status */}
        <Card
          title={<><WifiOutlined style={{ marginRight: 6, color: PALETTE.primaryBlue }} />Reader Connection Status</>}
          size="small" style={{ marginBottom: 16, borderRadius: 12 }}
          extra={<Text type="secondary" style={{ fontSize: 11 }}>{readers.filter(r => r.status === 'online').length} online of {readers.length}</Text>}
        >
          <Row gutter={[6, 6]}>
            {readers.map(r => (
              <Col key={r.id}>
                <Tooltip title={
                  <div>
                    <div>IP: {r.ip_address || 'N/A'}</div>
                    <div>Location: {r.location_name}</div>
                    <div>Scans (5m): {r.recent_scans || 0}</div>
                    <div>Last seen: {r.last_seen ? dayjs(r.last_seen).format('HH:mm:ss') : 'Never'}</div>
                  </div>
                }>
                  <Tag
                    icon={r.status === 'online' ? <WifiOutlined /> : <DisconnectOutlined />}
                    color={r.status === 'online' ? 'green' : r.status === 'idle' ? 'gold' : 'red'}
                    style={{ cursor: 'default', margin: 2 }}
                  >
                    {r.reader_name}
                    {r.reader_direction && (
                      <span style={{ marginLeft: 4 }}>
                        {r.reader_direction === 'Entry' ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                      </span>
                    )}
                  </Tag>
                </Tooltip>
              </Col>
            ))}
            {!readers.length && <Col><Text type="secondary">No readers configured</Text></Col>}
          </Row>
        </Card>

        {/* Location grid */}
        <Card
          title={
            <><EnvironmentOutlined style={{ marginRight: 6, color: PALETTE.primaryBlue }} />
              Hall / Zone Occupancy
              <Text type="secondary" style={{ fontSize: 11, marginLeft: 10 }}>· Click a card to see who is inside</Text>
            </>
          }
          size="small" style={{ marginBottom: 16, borderRadius: 12 }}
        >
          {locations.length === 0
            ? <Alert message="No locations found for this event" type="info" showIcon />
            : (
              <Row gutter={[12, 12]}>
                {locations.map(loc => (
                  <Col xs={12} sm={8} md={6} lg={4} key={loc.id}>
                    <LocationCard loc={loc} onClick={setSelectedLoc} />
                  </Col>
                ))}
              </Row>
            )
          }
        </Card>

        {/* Delegate table */}
        <Card
          title={<><UserOutlined style={{ marginRight: 6 }} />Delegate Tracking ({delegates.length})</>}
          style={{ borderRadius: 12 }}
          extra={<Tag color="blue">{delegates.filter(d => d.current_location).length} located</Tag>}
        >
          <Table
            columns={delegateCols}
            dataSource={delegates}
            rowKey="id"
            size="small"
            loading={loading}
            scroll={{ x: 900 }}
            pagination={{ pageSize: 20, showSizeChanger: true, showTotal: t => `${t} delegates` }}
          />
        </Card>
      </div>

      {/* Location Drawer */}
      <Drawer
        title={
          <Space>
            <EnvironmentOutlined style={{ color: PALETTE.primaryBlue, fontSize: 16 }} />
            <span style={{ fontWeight: 700, color: PALETTE.darkNavy }}>{selectedLoc?.location_name}</span>
            <Tag style={{ background: 'linear-gradient(135deg, #1e40af, #2563eb)', color: '#fff', border: 'none', fontWeight: 700, fontSize: 13, padding: '2px 10px', borderRadius: 20 }}>
              {drawerDelegates.length} inside
            </Tag>
          </Space>
        }
        placement="right"
        width={Math.min(600, window.innerWidth - 16)}
        open={!!selectedLoc}
        onClose={() => setSelectedLoc(null)}
        bodyStyle={{ padding: '16px 20px', background: PALETTE.lightBg }}
        headerStyle={{ background: '#fff', borderBottom: `2px solid ${PALETTE.lightBg}`, padding: '16px 20px' }}
      >
        {selectedLoc && (
          <div style={{ background: '#fff', borderRadius: 10, padding: '12px 16px', marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 1px 6px rgba(0,0,0,0.07)' }}>
            <div>
              <Text type="secondary" style={{ fontSize: 11 }}>Location Code</Text><br />
              <Text strong>{selectedLoc.location_code}</Text>
            </div>
            <Divider type="vertical" style={{ height: 32 }} />
            <div>
              <Text type="secondary" style={{ fontSize: 11 }}>Type</Text><br />
              <Tag color="geekblue">{selectedLoc.location_type || '—'}</Tag>
            </div>
            <Divider type="vertical" style={{ height: 32 }} />
            <div>
              <Text type="secondary" style={{ fontSize: 11 }}>Capacity</Text><br />
              <Text strong>{selectedLoc.capacity || '∞'}</Text>
            </div>
            <Divider type="vertical" style={{ height: 32 }} />
            <div style={{ textAlign: 'right' }}>
              <Text type="secondary" style={{ fontSize: 11 }}>Last updated</Text><br />
              <Text style={{ fontSize: 11, color: PALETTE.primaryBlue }}>{lastRefresh ? lastRefresh.format('HH:mm:ss') : '—'}</Text>
            </div>
          </div>
        )}
        {drawerDelegates.length > 0 ? (
          <Table columns={drawerCols} dataSource={drawerDelegates} rowKey="id" size="small"
            pagination={drawerDelegates.length > 10 ? { pageSize: 10, showTotal: t => `${t} delegates` } : false}
            style={{ borderRadius: 10, overflow: 'hidden' }} />
        ) : (
          <Alert
            message="No delegates currently in this location"
            description="Delegates will appear here once an RFID scan is recorded at this location."
            type="info" showIcon style={{ borderRadius: 10 }} />
        )}
      </Drawer>
    </div>
  );
}
